'use client';

import * as React from 'react';
import { Link } from 'react-router-dom';
import { FileSignature, Loader2, Plus, Save, Star, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    ClauseEditor,
    type ContractClauseTemplate,
} from '@/components/admin/crm-buy-rate-clause-editor';
import { trpc } from '@/lib/api/trpc';
import { ADMIN_ROUTES } from '@/config/router';

export default function AdminCrmContractTemplatePage() {
    const listQuery = trpc.partnerBuyRate.listContractTemplates.useQuery();
    const globalQuery = trpc.partnerBuyRate.getContractTemplate.useQuery();
    const utils = trpc.useUtils();

    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [template, setTemplate] = React.useState<ContractClauseTemplate | null>(null);
    const [dirty, setDirty] = React.useState(false);
    const [deleteId, setDeleteId] = React.useState<string | null>(null);

    const detailQuery = trpc.partnerBuyRate.getContractTemplateById.useQuery(
        { id: selectedId! },
        { enabled: !!selectedId },
    );

    React.useEffect(() => {
        if (!listQuery.data?.length || selectedId) return;
        const defaultItem =
            listQuery.data.find((t) => t.isDefault) ?? listQuery.data[0];
        setSelectedId(defaultItem.id);
    }, [listQuery.data, selectedId]);

    React.useEffect(() => {
        if (!detailQuery.data || dirty) return;
        setName(detailQuery.data.name);
        setDescription(detailQuery.data.description ?? '');
        setTemplate(structuredClone(detailQuery.data.template) as ContractClauseTemplate);
    }, [detailQuery.data, dirty]);

    React.useEffect(() => {
        if (selectedId) setDirty(false);
    }, [selectedId]);

    const invalidateAll = () => {
        utils.partnerBuyRate.listContractTemplates.invalidate();
        if (selectedId) {
            utils.partnerBuyRate.getContractTemplateById.invalidate({ id: selectedId });
        }
        utils.partnerBuyRate.getContractTemplate.invalidate();
    };

    const createMutation = trpc.partnerBuyRate.createContractTemplate.useMutation({
        onSuccess: (row) => {
            toast.success('Template created');
            setSelectedId(row.id);
            setDirty(false);
            invalidateAll();
        },
        onError: (err) => toast.error(err.message),
    });

    const updateMutation = trpc.partnerBuyRate.updateContractTemplate.useMutation({
        onSuccess: () => {
            toast.success('Template saved');
            setDirty(false);
            invalidateAll();
        },
        onError: (err) => toast.error(err.message),
    });

    const deleteMutation = trpc.partnerBuyRate.deleteContractTemplate.useMutation({
        onSuccess: () => {
            toast.success('Template deleted');
            setDeleteId(null);
            setSelectedId(null);
            setDirty(false);
            invalidateAll();
        },
        onError: (err) => toast.error(err.message),
    });

    const handleCreate = () => {
        const seed =
            globalQuery.data?.template ??
            (template ? structuredClone(template) : null);
        if (!seed) {
            toast.error('Could not load default clauses');
            return;
        }
        createMutation.mutate({
            name: 'New template',
            template: seed as ContractClauseTemplate,
        });
    };

    const handleSave = () => {
        if (!selectedId || !template) return;
        updateMutation.mutate({
            id: selectedId,
            name,
            description: description || null,
            template,
        });
    };

    const handleSetDefault = () => {
        if (!selectedId) return;
        updateMutation.mutate({ id: selectedId, isDefault: true });
    };

    const selectedMeta = listQuery.data?.find((t) => t.id === selectedId);

    return (
        <div className="mx-auto max-w-6xl space-y-6 p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Contract Templates</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Manage named clause libraries for partner buy rate agreements. Assign a default
                        template per partner on their billing page, or pick one when proposing.
                    </p>
                </div>
                <Button asChild variant="outline">
                    <Link to={ADMIN_ROUTES.PARTNERS}>Back to partners</Link>
                </Button>
            </div>

            <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between gap-2">
                            <CardTitle className="text-base">Library</CardTitle>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={createMutation.isPending || globalQuery.isLoading}
                                onClick={handleCreate}
                            >
                                {createMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Plus className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-1 p-2 pt-0">
                        {listQuery.isLoading ? (
                            [...Array(3)].map((_, i) => (
                                <Skeleton key={i} className="h-12 w-full" />
                            ))
                        ) : !listQuery.data?.length ? (
                            <p className="px-2 py-4 text-sm text-muted-foreground">
                                No templates yet. Create one from the platform default.
                            </p>
                        ) : (
                            listQuery.data.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => setSelectedId(item.id)}
                                    className={`flex w-full flex-col rounded-md border px-3 py-2 text-left transition-colors ${
                                        selectedId === item.id
                                            ? 'border-primary bg-primary/5'
                                            : 'border-transparent hover:bg-muted/60'
                                    }`}
                                >
                                    <span className="flex items-center gap-2 font-medium">
                                        {item.name}
                                        {item.isDefault && (
                                            <Badge variant="secondary" className="text-[10px]">
                                                Default
                                            </Badge>
                                        )}
                                    </span>
                                    {item.description && (
                                        <span className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                                            {item.description}
                                        </span>
                                    )}
                                </button>
                            ))
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <CardTitle className="flex items-center gap-2">
                                    <FileSignature className="h-5 w-5" />
                                    {selectedMeta?.name ?? 'Select a template'}
                                </CardTitle>
                                <CardDescription>
                                    {selectedMeta?.updatedByEmail
                                        ? `Last edited by ${selectedMeta.updatedByEmail}`
                                        : 'Edit clauses for the selected template'}
                                </CardDescription>
                            </div>
                            {selectedId && (
                                <div className="flex flex-wrap gap-2">
                                    {!selectedMeta?.isDefault && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={updateMutation.isPending}
                                            onClick={handleSetDefault}
                                        >
                                            <Star className="mr-1 h-4 w-4" />
                                            Set as default
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={deleteMutation.isPending}
                                        onClick={() => setDeleteId(selectedId)}
                                    >
                                        <Trash2 className="mr-1 h-4 w-4" />
                                        Delete
                                    </Button>
                                </div>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {!selectedId ? (
                            <p className="text-sm text-muted-foreground">
                                Select a template from the library or create a new one.
                            </p>
                        ) : detailQuery.isLoading || !template ? (
                            <div className="space-y-3">
                                {[...Array(4)].map((_, i) => (
                                    <Skeleton key={i} className="h-24 w-full" />
                                ))}
                            </div>
                        ) : (
                            <>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="template-name">Name</Label>
                                        <Input
                                            id="template-name"
                                            value={name}
                                            onChange={(e) => {
                                                setName(e.target.value);
                                                setDirty(true);
                                            }}
                                        />
                                    </div>
                                    <div className="space-y-2 sm:col-span-2">
                                        <Label htmlFor="template-description">Description</Label>
                                        <Textarea
                                            id="template-description"
                                            rows={2}
                                            value={description}
                                            placeholder="Optional note for operators (e.g. negotiated terms)"
                                            onChange={(e) => {
                                                setDescription(e.target.value);
                                                setDirty(true);
                                            }}
                                        />
                                    </div>
                                </div>

                                <ClauseEditor
                                    template={template}
                                    onChange={(next) => {
                                        setTemplate(next);
                                        setDirty(true);
                                    }}
                                    onReset={() => {
                                        if (detailQuery.data?.template) {
                                            setTemplate(
                                                structuredClone(
                                                    detailQuery.data.template,
                                                ) as ContractClauseTemplate,
                                            );
                                            setDirty(true);
                                        }
                                    }}
                                />

                                <div className="flex justify-end">
                                    <Button
                                        disabled={!dirty || updateMutation.isPending}
                                        onClick={handleSave}
                                    >
                                        {updateMutation.isPending ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : (
                                            <Save className="mr-2 h-4 w-4" />
                                        )}
                                        Save template
                                    </Button>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this template?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Partners assigned to this template must be updated first. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => deleteId && deleteMutation.mutate({ id: deleteId })}
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
